import { z } from 'zod';
import { TASK_TYPES } from './constants.js';
import { objectId } from './schemas.js';
/**
 * The client portal: a project's window for the person paying for it.
 *
 * A client is not a user of the workspace and never becomes one. They hold a
 * *client access* — a named pass onto exactly one project — and nothing it
 * signs them into can reach the workspace API. What they can do is narrow on
 * purpose: read how the work is going, and raise requests the team answers.
 *
 * One access supports every way in a team might want to hand one out:
 *
 * - **link only** — the link is the whole credential, for low-stakes boards;
 * - **link + password** — the link finds the project, the password proves it
 *   is really them, so a forwarded email is not enough;
 * - **username + password** — no link at all; the client signs in at the
 *   portal's front door, which is what a client with several projects expects.
 */
/* ------------------------------ access ---------------------------------- */
export const CLIENT_ACCESS_MODES = ['link', 'link_password', 'credentials'];
export const CLIENT_ACCESS_MODE_INFO = {
    link: { label: 'Link only', hint: 'Anyone holding the link can open the portal' },
    link_password: { label: 'Link + password', hint: 'The link also asks for a password' },
    credentials: { label: 'Username + password', hint: 'The client signs in at the portal page' },
};
/**
 * Which tasks a client sees.
 *
 * `labelled` exists because most boards carry internal work — refactors, a
 * spike nobody promised — that reads badly to the person paying. Tagging the
 * tasks worth showing with {@link CLIENT_VISIBLE_LABEL} uses the labels every
 * task already has, so the team needs no new control to curate the view.
 */
export const CLIENT_TASK_VISIBILITIES = ['all', 'labelled'];
export const CLIENT_VISIBLE_LABEL = 'client';
const username = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9._-]{3,40}$/, 'Usernames are 3–40 letters, numbers, dots, dashes or underscores');
const password = z.string().min(8, 'Use at least 8 characters').max(128);
export const createClientAccessSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    company: z.string().trim().max(120).optional(),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
    mode: z.enum(CLIENT_ACCESS_MODES),
    username: username.optional(),
    password: password.optional(),
    canRequestChanges: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    if (value.mode !== 'link' && !value.password) {
        ctx.addIssue({ code: 'custom', path: ['password'], message: 'This access needs a password' });
    }
    if (value.mode === 'credentials' && !value.username) {
        ctx.addIssue({ code: 'custom', path: ['username'], message: 'This access needs a username' });
    }
});
/** Every field optional; the mode rules are re-checked against the stored access. */
export const updateClientAccessSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    company: z.string().trim().max(120).nullable().optional(),
    email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
    mode: z.enum(CLIENT_ACCESS_MODES).optional(),
    username: username.optional(),
    password: password.optional(),
    canRequestChanges: z.boolean().optional(),
});
export const updateClientPortalSettingsSchema = z.object({
    taskVisibility: z.enum(CLIENT_TASK_VISIBILITIES),
});
/* ------------------------------ session --------------------------------- */
export const startClientSessionSchema = z.union([
    z.object({
        linkToken: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/),
        password: z.string().max(128).optional(),
    }),
    z.object({ username, password: z.string().min(1).max(128) }),
]);
/* ------------------------------ tickets --------------------------------- */
export const CLIENT_TICKET_KINDS = ['change_request', 'question', 'issue'];
export const CLIENT_TICKET_KIND_INFO = {
    change_request: { label: 'Change request', hint: 'Something should work or look differently' },
    question: { label: 'Question', hint: 'You need an answer from the team' },
    issue: { label: 'Issue', hint: 'Something is broken or not as agreed' },
};
/**
 * Where a request stands.
 *
 * `approved` means the team agreed to do it: approving puts a task in the
 * project's backlog. `declined` is separate from `closed` because a client
 * deserves to know the difference between "done" and "we are not doing this".
 */
export const CLIENT_TICKET_STATUSES = [
    'open',
    'approved',
    'in_progress',
    'waiting_on_client',
    'resolved',
    'declined',
    'closed',
];
export const CLIENT_TICKET_STATUS_LABEL = {
    open: 'Open',
    approved: 'Approved',
    in_progress: 'In progress',
    waiting_on_client: 'Waiting on client',
    resolved: 'Resolved',
    declined: 'Declined',
    closed: 'Closed',
};
/** Statuses that count as finished; a client reply to one reopens it. */
export const CLIENT_TICKET_FINISHED = ['resolved', 'declined', 'closed'];
const body = z.string().trim().min(1).max(10_000);
export const createClientTicketSchema = z.object({
    kind: z.enum(CLIENT_TICKET_KINDS),
    title: z.string().trim().min(3).max(200),
    body,
});
export const clientTicketReplySchema = z.object({ body });
export const memberTicketReplySchema = z.object({
    body,
    internal: z.boolean().default(false),
});
export const updateClientTicketSchema = z.object({
    status: z.enum(CLIENT_TICKET_STATUSES),
});
export const clientTicketListQuerySchema = z.object({
    status: z.enum(['active', 'finished', 'all']).default('active'),
});
export const approveClientTicketSchema = z.object({
    type: z.enum(TASK_TYPES).default('feature'),
    milestoneId: objectId.optional(),
});
